// src/utils/artkivLabels.js

// VERSION FINAL FIX — includes medium (technique) translation

import { getArtTypeCanonicalKey } from "./artTypeTaxonomy";

const humanizeKey = (value) => {
  if (!value) return "—";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Techniques et supports français → clés de la taxonomie (« huile_sur_toile » → « oil_on_canvas »).
const FR_MEDIUM_TOKENS = {
  huile: "oil", acrylique: "acrylic", aquarelle: "watercolor", gouache: "gouache", encre: "ink", fusain: "charcoal",
  crayon: "pencil", pastel: "pastel", collage: "collage", technique_mixte: "mixed_media", techniques_mixtes: "mixed_media", carnet: "sketchbook", tempera: "tempera",
  toile: "canvas", papier: "paper", bois: "wood", carton: "cardboard", panneau: "panel", lin: "linen", cuivre: "copper", verre: "glass",
};
export const canonicalMediumKey = (key) => {
  const k = String(key || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[\s-]+/g, "_");
  const m = k.match(/^(.+?)_sur_(.+)$/);
  if (m) {
    const tech = FR_MEDIUM_TOKENS[m[1]];
    const support = FR_MEDIUM_TOKENS[m[2]];
    if (tech && support) return `${tech}_on_${support}`;
    return k;
  }
  return FR_MEDIUM_TOKENS[k] || k;
};

export const ARTKIV_LABEL_PATHS = {
  typeOfArt: "artwork.typeOfArt",
  availability: "artwork.availability",
  condition: "artwork.condition",
  mediumCategory: "artworkEdit.detail",
  medium: "artwork.mediums", // NEW
};

export const getArtkivLabel = (type, key, t, fallback = "—") => {
  if (!key) return fallback;

  const basePath = ARTKIV_LABEL_PATHS[type];

  if (!basePath || typeof t !== "function") {
    return humanizeKey(key);
  }

  // --- MEDIUM (TECHNIQUE) ---
  if (type === "medium") {
    const MISSING = "__artkiv_missing__";
    const direct = t(`${basePath}.${key}`, { defaultValue: MISSING });
    if (direct && direct !== MISSING) return direct;
    // Clé issue d'une saisie française (ex. « gouache_sur_papier ») : on la rapproche de la clé canonique (« gouache_on_paper »).
    const alias = canonicalMediumKey(key);
    if (alias && alias !== key) {
      const viaAlias = t(`${basePath}.${alias}`, { defaultValue: MISSING });
      if (viaAlias && viaAlias !== MISSING) return viaAlias;
    }
    // Plusieurs techniques saisies à la suite (« Carnet, techniques mixtes ») : chacune est traduite, puis réunies.
    if (String(key).includes(",")) {
      return String(key).split(",").map((p) => p.trim()).filter(Boolean)
        .map((p) => getArtkivLabel("medium", p, t, fallback)).join(", ");
    }
    return humanizeKey(key);
  }

  // --- MEDIUM CATEGORY ---
  if (type === "mediumCategory") {
    const map = {
      painting: "mediumCategoryPainting",
      photography: "mediumCategoryPhotography",
      sculpture: "mediumCategorySculpture",
      prints: "mediumCategoryPrints",
      print: "mediumCategoryPrints",
      drawing: "mediumCategoryDrawing",
      work_on_paper: "mediumCategoryWorkOnPaper",
      mixed_media: "mediumCategoryMixedMedia",
      installation: "mediumCategoryInstallation",
      film_video: "mediumCategoryFilmVideo",
      digital: "mediumCategoryNft",
      nft: "mediumCategoryNft",
      ceramics: "mediumCategoryCeramics",
      textile: "mediumCategoryTextile",
      design: "mediumCategoryDesign",
      other: "mediumCategoryOther",
    };

    const i18nKey = map[key];

    return i18nKey
      ? t(`${basePath}.${i18nKey}`, { defaultValue: humanizeKey(key) })
      : humanizeKey(key);
  }

  return t(`${basePath}.${key}`, {
    defaultValue: humanizeKey(key),
  });
};

// HELPERS
// Le « type d'art » est un champ libre : on canonicalise TOUJOURS avant d'étiqueter
// (voir src/utils/artTypeTaxonomy.js). Le vide reste « — » (pas « Non renseigné »).
export const getTypeOfArtLabel = (key, t) =>
  key ? getArtkivLabel("typeOfArt", getArtTypeCanonicalKey(key), t) : getArtkivLabel("typeOfArt", key, t);

export const getAvailabilityLabel = (key, t) =>
  getArtkivLabel("availability", key, t);

export const getConditionLabel = (key, t) =>
  getArtkivLabel("condition", key, t);

export const getMediumCategoryLabelI18n = (key, t) =>
  getArtkivLabel("mediumCategory", key, t);

export const getMediumLabel = (key, t) =>
  getArtkivLabel("medium", key, t);

// Alias rétrocompatible : certains composants importent encore getTechniqueLabel.
export const getTechniqueLabel = (key, t) =>
  getMediumLabel(key, t);
